import React, { useEffect, useRef, useState } from 'react';
import './Auth.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

/* ── Dropdown option sets (same as original Onboarding) ── */
const QUALIFICATIONS = [
  'High School / 10th',
  'Intermediate / 12th',
  'Diploma',
  'Undergraduate (Pursuing)',
  "Graduate / Bachelor's Degree",
  "Post Graduate / Master's Degree",
  'PhD / Doctorate',
  'Certification Course',
  'Other',
];
const STREAMS = [
  'Computer Science / IT', 'Engineering', 'Commerce', 'Science',
  'Arts / Humanities', 'Management / Business', 'Medical / Healthcare',
  'Law', 'Education / Teaching', 'Design / UI-UX', 'Finance / Accounting',
  'Marketing', 'Data Science / AI', 'Cybersecurity',
  'Hospitality / Hotel Management', 'Animation / Multimedia',
  'Agriculture', 'Architecture', 'Psychology', 'Other',
];
const EXPERIENCES = [
  'Fresher', 'Less than 1 year', '1-2 years',
  '2-5 years', '5-10 years', '10+ years', 'Other',
];

function Particles() {
  const particles = Array.from({ length: 14 }, (_, i) => ({
    id: i,
    left: `${(i * 7 + 4) % 100}%`,
    size: `${(i % 4) + 2}px`,
    duration: `${(i % 10) + 9}s`,
    delay: `${(i % 8)}s`,
  }));
  return (
    <div className="auth-particles" aria-hidden>
      {particles.map((p) => (
        <span key={p.id} className="auth-particle"
          style={{ left: p.left, width: p.size, height: p.size, animationDuration: p.duration, animationDelay: p.delay }}
        />
      ))}
    </div>
  );
}

function IconSun() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></svg>; }
function IconMoon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" /></svg>; }
function IconBack() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden><path d="M19 12H5M12 5l-7 7 7 7" /></svg>; }
function IconEye()  { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>; }
function IconEyeOff() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>; }
function IconArrow() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden><path d="M5 12h14M12 5l7 7-7 7" /></svg>; }
function IconChevron() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden><polyline points="6 9 12 15 18 9" /></svg>; }

function FieldSelect({ id, label, value, onChange, options, error, placeholder }) {
  return (
    <div className="auth-field">
      <label className="auth-label" htmlFor={id}>{label}</label>
      <div className="auth-select-wrap">
        <select id={id} className={`auth-select${error ? ' has-error' : ''}`}
          value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{placeholder}</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <span className="auth-select-chevron"><IconChevron /></span>
      </div>
      {error && <span className="auth-error" role="alert">{error}</span>}
    </div>
  );
}

/**
 * SignUpForm
 * The robot is now rendered once at the App root level (persistent).
 * Props:
 *   onBack        : () => void
 *   onSuccess     : (user, token) => void
 *   isDarkMode    : boolean
 *   onToggleTheme : () => void
 */
export function SignUpForm({ onBack, onSuccess, isDarkMode, onToggleTheme }) {
  const [form, setForm] = useState({
    firstName: '', middleName: '', lastName: '',
    dateOfBirth: '',
    email: '', password: '', confirmPassword: '',
    qualification: '', customQualification: '',
    stream: '', customStream: '',
    experience: '', customExperience: '',
  });
  const [errors, setErrors]     = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPw, setShowPw]     = useState(false);
  const [showCPw, setShowCPw]   = useState(false);
  const firstRef = useRef(null);

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    if (apiError) setApiError('');
  }

  function validate() {
    const e = {};
    if (!form.firstName.trim())  e.firstName  = 'First name is required.';
    if (!form.lastName.trim())   e.lastName   = 'Last name is required.';
    if (!form.email.trim())      e.email      = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Please enter a valid email.';
    if (!form.password)          e.password   = 'Password is required.';
    else if (form.password.length < 6) e.password = 'Password must be at least 6 characters.';
    if (!form.confirmPassword)   e.confirmPassword = 'Please confirm your password.';
    else if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
    if (!form.qualification)     e.qualification = 'Please select your qualification.';
    if (form.qualification === 'Other' && !form.customQualification.trim())
      e.customQualification = 'Please specify your qualification.';
    if (!form.stream)            e.stream     = 'Please select your stream.';
    if (form.stream === 'Other' && !form.customStream.trim())
      e.customStream = 'Please specify your stream.';
    if (!form.experience)        e.experience = 'Please select your experience level.';
    if (form.experience === 'Other' && !form.customExperience.trim())
      e.customExperience = 'Please specify your experience.';
    return e;
  }

  async function handleSubmit(ev) {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    setApiError('');
    try {
      const res  = await fetch(`${API_BASE}/auth/register`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          firstName:          form.firstName.trim(),
          middleName:         form.middleName.trim(),
          lastName:           form.lastName.trim(),
          dateOfBirth:        form.dateOfBirth,
          email:              form.email.trim(),
          password:           form.password,
          qualification:      form.qualification,
          customQualification: form.customQualification.trim(),
          stream:             form.stream,
          customStream:       form.customStream.trim(),
          experience:         form.experience,
          customExperience:   form.customExperience.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Registration failed. Please try again.');
      onSuccess(data.user, data.token);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function pwInput(id, label, field, showState, setShowState, autoComplete, placeholder) {
    return (
      <div className="auth-field">
        <label className="auth-label" htmlFor={id}>{label}</label>
        <div className="auth-pw-wrap">
          <input id={id} type={showState ? 'text' : 'password'}
            className={`auth-input${errors[field] ? ' has-error' : ''}`}
            placeholder={placeholder}
            value={form[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            autoComplete={autoComplete}
            required
          />
          <button type="button" className="auth-pw-toggle" onClick={() => setShowState((v) => !v)}
            aria-label={showState ? 'Hide password' : 'Show password'}>
            {showState ? <IconEyeOff /> : <IconEye />}
          </button>
        </div>
        {errors[field] && <span className="auth-error" role="alert">{errors[field]}</span>}
      </div>
    );
  }

  return (
    <div className="auth-root">
      <Particles />
      <button type="button" className="auth-theme-btn" onClick={onToggleTheme}
        aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
        {isDarkMode ? <IconSun /> : <IconMoon />}
      </button>

      {/* ── Left ── */}
      <div className="auth-left">
        <div className="auth-brand" style={{ marginBottom: 20 }}>
          <div className="auth-brand-badge"><span className="auth-brand-dot" />AI Powered · Career Guidance</div>
          <h1 className="auth-brand-title">Create Your<br />Account</h1>
          <p className="auth-brand-sub">Fill in your details to get AI-powered career advice tailored just for you.</p>
        </div>

        <div className="auth-form-container">
          <button type="button" className="auth-back" onClick={onBack}><IconBack /> Back</button>

          {apiError && <div className="auth-api-error" role="alert">⚠ {apiError}</div>}

          <form className="auth-form" onSubmit={handleSubmit} noValidate>

            {/* ── Name row ── */}
            <div className="auth-form-row">
              <div className="auth-field">
                <label className="auth-label" htmlFor="su-firstname">First Name</label>
                <input id="su-firstname" ref={firstRef} type="text"
                  className={`auth-input${errors.firstName ? ' has-error' : ''}`}
                  placeholder="e.g. Aditya"
                  value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)}
                  autoComplete="given-name" required
                />
                {errors.firstName && <span className="auth-error" role="alert">{errors.firstName}</span>}
              </div>
              <div className="auth-field">
                <label className="auth-label" htmlFor="su-middlename">
                  Middle Name <span className="auth-optional">(optional)</span>
                </label>
                <input id="su-middlename" type="text"
                  className="auth-input"
                  placeholder="e.g. Kumar"
                  value={form.middleName} onChange={(e) => handleChange('middleName', e.target.value)}
                  autoComplete="additional-name"
                />
              </div>
            </div>

            <div className="auth-form-row">
              <div className="auth-field">
                <label className="auth-label" htmlFor="su-lastname">Last Name</label>
                <input id="su-lastname" type="text"
                  className={`auth-input${errors.lastName ? ' has-error' : ''}`}
                  placeholder="e.g. Sharma"
                  value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)}
                  autoComplete="family-name" required
                />
                {errors.lastName && <span className="auth-error" role="alert">{errors.lastName}</span>}
              </div>
              <div className="auth-field">
                <label className="auth-label" htmlFor="su-dob">
                  Date of Birth <span className="auth-optional">(optional)</span>
                </label>
                <input id="su-dob" type="date"
                  className="auth-input"
                  value={form.dateOfBirth} onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  autoComplete="bday"
                  style={{ colorScheme: isDarkMode ? 'dark' : 'light' }}
                />
              </div>
            </div>

            {/* ── Email ── */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="su-email">Email</label>
              <input id="su-email" type="email"
                className={`auth-input${errors.email ? ' has-error' : ''}`}
                placeholder="you@example.com"
                value={form.email} onChange={(e) => handleChange('email', e.target.value)}
                autoComplete="email" required
              />
              {errors.email && <span className="auth-error" role="alert">{errors.email}</span>}
            </div>

            {/* ── Password row ── */}
            <div className="auth-form-row">
              {pwInput('su-password',  'Password',         'password',        showPw,  setShowPw,  'new-password',     'Min. 6 characters')}
              {pwInput('su-cpw',       'Confirm Password', 'confirmPassword', showCPw, setShowCPw, 'new-password',     'Repeat password')}
            </div>

            {/* ── Qualification ── */}
            <FieldSelect id="su-qualification" label="Highest Qualification"
              value={form.qualification} onChange={(v) => handleChange('qualification', v)}
              options={QUALIFICATIONS} error={errors.qualification}
              placeholder="— Select qualification —"
            />
            {form.qualification === 'Other' && (
              <div className="auth-field">
                <input id="su-customQualification" type="text"
                  className={`auth-input${errors.customQualification ? ' has-error' : ''}`}
                  placeholder="Please specify your qualification"
                  value={form.customQualification}
                  onChange={(e) => handleChange('customQualification', e.target.value)}
                />
                {errors.customQualification && <span className="auth-error" role="alert">{errors.customQualification}</span>}
              </div>
            )}

            {/* ── Stream ── */}
            <FieldSelect id="su-stream" label="Stream / Field of Study"
              value={form.stream} onChange={(v) => handleChange('stream', v)}
              options={STREAMS} error={errors.stream}
              placeholder="— Select stream —"
            />
            {form.stream === 'Other' && (
              <div className="auth-field">
                <input id="su-customStream" type="text"
                  className={`auth-input${errors.customStream ? ' has-error' : ''}`}
                  placeholder="Please specify your stream"
                  value={form.customStream}
                  onChange={(e) => handleChange('customStream', e.target.value)}
                />
                {errors.customStream && <span className="auth-error" role="alert">{errors.customStream}</span>}
              </div>
            )}

            {/* ── Experience ── */}
            <FieldSelect id="su-experience" label="Work Experience"
              value={form.experience} onChange={(v) => handleChange('experience', v)}
              options={EXPERIENCES} error={errors.experience}
              placeholder="— Select experience —"
            />
            {form.experience === 'Other' && (
              <div className="auth-field">
                <input id="su-customExperience" type="text"
                  className={`auth-input${errors.customExperience ? ' has-error' : ''}`}
                  placeholder="Please specify your experience"
                  value={form.customExperience}
                  onChange={(e) => handleChange('customExperience', e.target.value)}
                />
                {errors.customExperience && <span className="auth-error" role="alert">{errors.customExperience}</span>}
              </div>
            )}

            <div className="auth-submit-wrap">
              <button id="signup-submit-btn" type="submit" className="auth-submit" disabled={loading}>
                {loading ? (<><span className="auth-spinner" /> Creating account…</>) : (<>Create Account &amp; Start Counseling <IconArrow /></>)}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ── Right — Greeting overlay (robot is behind, rendered by App root) ── */}
      <div className="auth-right">
        <div className="auth-greeting" role="complementary">
          <p className="auth-greeting-title">Hello! I'm your AI Career Counselor 👋</p>
          <p className="auth-greeting-sub">
            Tell me about yourself so I can give you the best career advice.
            Let's build your career path together!
          </p>
        </div>
      </div>
    </div>
  );
}
